import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HomeDashboardLayout from "../HomeDashboardLayout";
import { createAssistantSession, fetchAssistantConversation, fetchAssistantSessions, fetchAssistantUsage, isAssistantUsageBlocked, runAssistantTool, saveAssistantMessage } from "../../services/assistantRealtimeService";
import { BrowserRealtimeVoice } from "./browserRealtimeVoice";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "./VoiceChat.css";

const ACCESS_MESSAGE = "Your daily AI credits have been exhausted. Upgrade to Premium or try again tomorrow.";
const VOICE_MESSAGE = "Tap the microphone to start a voice conversation.";

function relativeDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days < 1) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const preview = (session) => String(session?.latest_message?.message || "No message yet").trim();

export default function VoiceChat() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => window.innerWidth <= 900);
  const [search, setSearch] = useState("");
  const [sessions, setSessions] = useState([]);
  const [historyStatus, setHistoryStatus] = useState("loading");
  const [historyError, setHistoryError] = useState("");
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conversationStatus, setConversationStatus] = useState("idle");
  const [conversationError, setConversationError] = useState("");
  const [sessionStatus, setSessionStatus] = useState("loading");
  const [sessionError, setSessionError] = useState("");
  const [sessionLogId, setSessionLogId] = useState(null);
  const [usage, setUsage] = useState(null);
  const [voiceStatus, setVoiceStatus] = useState("idle");
  const [micMuted, setMicMuted] = useState(false);
  const [liveMessages, setLiveMessages] = useState([]);
  const [voiceError, setVoiceError] = useState("");
  const [copiedMessage, setCopiedMessage] = useState(null);
  const generationRef = useRef(0);
  const sessionRequestRef = useRef(null);
  const historyRequestRef = useRef(null);
  const conversationRequestRef = useRef(null);
  const historyLoadingRef = useRef(false);
  const mountedRef = useRef(false);
  const conversationRef = useRef(null);
  const historyCloseRef = useRef(null);
  const historyOpenRef = useRef(null);
  const sessionRef = useRef(null);
  const usageRef = useRef(null);
  const clientRef = useRef(null);
  const responseTimerRef = useRef(null);
  const responseActiveRef = useRef(false);
  const audioOutputRef = useRef(false);
  const playbackPendingRef = useRef(false);
  const toolActiveRef = useRef(false);
  const awaitingToolResponseRef = useRef(false);
  const pendingUserIdRef = useRef(null);
  const assistantIdRef = useRef(null);
  const liveMessagesRef = useRef([]);
  const savedItemsRef = useRef(new Set());
  const savingItemsRef = useRef(new Set());
  const handledCallsRef = useRef(new Set());
  const saveQueueRef = useRef(Promise.resolve());
  const sequenceRef = useRef(0);
  const turnRevisionRef = useRef(0);
  const listenLoopRef = useRef(false);

  const updateLiveMessage = useCallback((id, patch) => {
    const index = liveMessagesRef.current.findIndex((item) => item.id === id);
    const next = [...liveMessagesRef.current];
    if (index >= 0) next[index] = { ...next[index], ...patch };
    else next.push({ id, role: patch.role || "assistant", text: "", createdAt: Date.now(), ...patch });
    liveMessagesRef.current = next;
    setLiveMessages(next);
  }, []);

  const queueSave = useCallback((id, role, text) => {
    const sessionId = sessionRef.current?.session_log_id;
    const generation = generationRef.current;
    if (!sessionId || !text?.trim() || savedItemsRef.current.has(id) || savingItemsRef.current.has(id)) return saveQueueRef.current;
    savingItemsRef.current.add(id);
    saveQueueRef.current = saveQueueRef.current.catch(() => {}).then(async () => {
      if (generation !== generationRef.current) return;
      const sequence = sequenceRef.current + 1;
      try {
        await saveAssistantMessage({ sessionLogId: sessionId, role, message: text.trim(), sequence });
        if (generation === generationRef.current) {
          sequenceRef.current = sequence;
          savedItemsRef.current.add(id);
        }
      } catch (error) {
        if (generation === generationRef.current) setVoiceError(error.message || "A message could not be saved.");
      } finally {
        savingItemsRef.current.delete(id);
      }
    });
    return saveQueueRef.current;
  }, []);

  const loadHistory = useCallback(async () => {
    if (historyLoadingRef.current) return;
    historyLoadingRef.current = true;
    const request = new AbortController();
    historyRequestRef.current = request;
    setHistoryStatus("loading");
    setHistoryError("");
    try {
      const nextSessions = await fetchAssistantSessions({ signal: request.signal });
      if (mountedRef.current && !request.signal.aborted) {
        setSessions(nextSessions);
        setHistoryStatus("success");
      }
    } catch (error) {
      if (mountedRef.current && !request.signal.aborted) {
        setHistoryError(error.message || "Could not load conversations.");
        setHistoryStatus("error");
      }
    } finally {
      if (historyRequestRef.current === request) historyLoadingRef.current = false;
    }
  }, []);

  const startNewSession = useCallback(async () => {
    const generation = ++generationRef.current;
    clientRef.current?.close();
    clientRef.current = null;
    if (responseTimerRef.current) clearTimeout(responseTimerRef.current);
    responseTimerRef.current = null;
    responseActiveRef.current = false;
    audioOutputRef.current = false;
    playbackPendingRef.current = false;
    toolActiveRef.current = false;
    awaitingToolResponseRef.current = false;
    pendingUserIdRef.current = null;
    assistantIdRef.current = null;
    sessionRef.current = null;
    usageRef.current = null;
    sequenceRef.current = 0;
    turnRevisionRef.current = 0;
    listenLoopRef.current = false;
    savedItemsRef.current = new Set();
    savingItemsRef.current = new Set();
    handledCallsRef.current = new Set();
    saveQueueRef.current = Promise.resolve();
    liveMessagesRef.current = [];
    setLiveMessages([]);
    setVoiceStatus("idle");
    setVoiceError("");
    setMicMuted(false);
    sessionRequestRef.current?.abort();
    conversationRequestRef.current?.abort();
    const request = new AbortController();
    sessionRequestRef.current = request;
    setSelectedHistoryId(null);
    setMessages([]);
    setSessionLogId(null);
    setUsage(null);
    setSessionError("");
    setSessionStatus("loading");
    setConversationStatus("idle");
    setMobileSidebarOpen(false);
    try {
      const session = await createAssistantSession({ signal: request.signal });
      if (!session?.session_log_id || !session?.client_secret?.value) {
        throw new Error("The voice session is missing its session ID or connection credential.");
      }
      const nextUsage = session.usage || await fetchAssistantUsage({ signal: request.signal });
      if (!mountedRef.current || request.signal.aborted || generation !== generationRef.current) return;
      setSessionLogId(session.session_log_id);
      setUsage(nextUsage || null);
      usageRef.current = nextUsage || null;
      if (isAssistantUsageBlocked(nextUsage)) {
        setSessionError(nextUsage?.message || ACCESS_MESSAGE);
        setSessionStatus("blocked");
      } else {
        sessionRef.current = session;
        setSessionStatus("ready");
      }
      void loadHistory();
    } catch (error) {
      if (mountedRef.current && !request.signal.aborted && generation === generationRef.current) {
        setSessionError(error.message || "Could not start Chat Voice.");
        setSessionStatus(error.kind === "access" || Number(error.backend?.block_status) === 1 ? "blocked" : "error");
      }
    }
  }, [loadHistory]);

  useEffect(() => {
    mountedRef.current = true;
    void startNewSession();
    void loadHistory();
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      sessionRequestRef.current?.abort();
      historyRequestRef.current?.abort();
      historyLoadingRef.current = false;
      conversationRequestRef.current?.abort();
      if (responseTimerRef.current) clearTimeout(responseTimerRef.current);
      clientRef.current?.close();
      clientRef.current = null;
    };
  }, [startNewSession, loadHistory]);

  const openConversation = useCallback(async (sessionId) => {
    if (sessionId == null) return;
    clientRef.current?.close();
    clientRef.current = null;
    listenLoopRef.current = false;
    if (responseTimerRef.current) clearTimeout(responseTimerRef.current);
    responseTimerRef.current = null;
    setVoiceStatus("idle");
    conversationRequestRef.current?.abort();
    const request = new AbortController();
    conversationRequestRef.current = request;
    setSelectedHistoryId(sessionId);
    setMessages([]);
    setConversationStatus("loading");
    setConversationError("");
    setMobileSidebarOpen(false);
    try {
      const nextMessages = await fetchAssistantConversation(sessionId, { signal: request.signal });
      if (mountedRef.current && !request.signal.aborted) {
        setMessages(nextMessages);
        setConversationStatus("success");
      }
    } catch (error) {
      if (mountedRef.current && !request.signal.aborted) {
        setConversationError(error.message || "Could not load this conversation.");
        setConversationStatus("error");
      }
    }
  }, []);

  const handleRealtimeEvent = useCallback(async (event) => {
    const generation = generationRef.current;
    const type = event?.type;
    if (!type || !mountedRef.current || !clientRef.current || clientRef.current.closed) return;
    const matchesSession = () => mountedRef.current && generation === generationRef.current && !clientRef.current?.closed;
    const stopResponseTimer = () => {
      if (responseTimerRef.current) clearTimeout(responseTimerRef.current);
      responseTimerRef.current = null;
    };
    if (type === "input_audio_buffer.speech_started") {
      stopResponseTimer();
      turnRevisionRef.current += 1;
      if (!responseActiveRef.current) setVoiceStatus("listening");
      return;
    }
    if (type === "input_audio_buffer.speech_stopped" || type === "input_audio_buffer.committed") {
      const id = pendingUserIdRef.current || event.item_id || `voice-${Date.now()}`;
      pendingUserIdRef.current = id;
      if (!liveMessagesRef.current.some((item) => item.id === id)) {
        updateLiveMessage(id, { role: "user", text: "Voice message", isStreaming: true });
      }
      return;
    }
    if (type === "conversation.item.created" && event.item?.type === "message") {
      const item = event.item;
      const text = (Array.isArray(item.content) ? item.content : [])
        .map((part) => part.text || part.transcript || "").filter(Boolean).join("\n").trim();
      if (text && item.id && (item.role === "user" || item.role === "assistant")) {
        const id = item.role === "assistant" ? assistantIdRef.current || item.id : pendingUserIdRef.current || item.id;
        updateLiveMessage(id, { role: item.role, text, isStreaming: false });
      }
      return;
    }
    if (type === "conversation.item.input_audio_transcription.delta") {
      const id = pendingUserIdRef.current || event.item_id;
      if (id) {
        const prior = liveMessagesRef.current.find((item) => item.id === id)?.text || "";
        updateLiveMessage(id, { role: "user", text: prior === "Voice message" ? String(event.delta || "") : prior + String(event.delta || ""), isStreaming: true });
      }
      return;
    }
    if (type === "conversation.item.input_audio_transcription.completed") {
      const id = pendingUserIdRef.current || event.item_id || `voice-${Date.now()}`;
      const prior = liveMessagesRef.current.find((item) => item.id === id)?.text || "";
      const incoming = String(event.transcript || "").trim();
      const combined = prior && prior !== "Voice message" && incoming && !incoming.startsWith(prior) && !prior.endsWith(incoming)
        ? `${prior} ${incoming}` : incoming || prior;
      const text = combined.trim();
      if ((text.match(/[\p{L}\p{N}]/gu) || []).length < 3) {
        const next = liveMessagesRef.current.filter((item) => item.id !== id);
        liveMessagesRef.current = next;
        setLiveMessages(next);
        pendingUserIdRef.current = null;
        return;
      }
      updateLiveMessage(id, { role: "user", text, isStreaming: false });
      try {
        const nextUsage = await fetchAssistantUsage();
        if (!matchesSession()) return;
        usageRef.current = nextUsage;
        setUsage(nextUsage);
      } catch (error) {
        if (matchesSession()) setVoiceError(error.message || "Could not refresh credits; using the last known access decision.");
      }
      if (!matchesSession()) return;
      if (isAssistantUsageBlocked(usageRef.current)) {
        stopResponseTimer();
        clientRef.current?.close();
        clientRef.current = null;
        const next = liveMessagesRef.current.filter((item) => item.id !== id);
        liveMessagesRef.current = next;
        setLiveMessages(next);
        setSessionError(usageRef.current?.message || ACCESS_MESSAGE);
        setSessionStatus("blocked");
        setVoiceStatus("idle");
        return;
      }
      stopResponseTimer();
      const revision = ++turnRevisionRef.current;
      responseTimerRef.current = setTimeout(async () => {
        if (!matchesSession() || revision !== turnRevisionRef.current || responseActiveRef.current || toolActiveRef.current) return;
        const finalText = liveMessagesRef.current.find((item) => item.id === id)?.text || text;
        clientRef.current.setMicrophoneEnabled(false);
        pendingUserIdRef.current = null;
        setVoiceStatus("thinking");
        await queueSave(id, "user", finalText);
        if (!matchesSession() || revision !== turnRevisionRef.current) return;
        clientRef.current.send({ type: "response.create", response: { output_modalities: ["audio"] } });
      }, 900);
      return;
    }
    if (type === "conversation.item.input_audio_transcription.failed") return;
    if (type === "response.created") {
      responseActiveRef.current = true;
      awaitingToolResponseRef.current = false;
      audioOutputRef.current = false;
      playbackPendingRef.current = false;
      assistantIdRef.current = event.response?.id || `assistant-${Date.now()}`;
      clientRef.current.setMicrophoneEnabled(false);
      setVoiceStatus("thinking");
      return;
    }
    if (type === "response.audio_transcript.delta" || type === "response.output_audio_transcript.delta") {
      const id = assistantIdRef.current || event.item_id || `assistant-${Date.now()}`;
      assistantIdRef.current = id;
      const prior = liveMessagesRef.current.find((item) => item.id === id)?.text || "";
      updateLiveMessage(id, { role: "assistant", text: (prior === "Searching..." ? "" : prior) + String(event.delta || ""), isStreaming: true });
      setVoiceStatus("speaking");
      clientRef.current.resumePlayback();
      return;
    }
    if (type === "response.audio_transcript.done" || type === "response.output_audio_transcript.done") {
      const id = assistantIdRef.current || event.item_id;
      if (id) {
        const text = String(event.transcript || liveMessagesRef.current.find((item) => item.id === id)?.text || "").trim();
        updateLiveMessage(id, { role: "assistant", text, isStreaming: false });
        queueSave(id, "assistant", text);
        void loadHistory();
      }
      return;
    }
    if (type === "output_audio_buffer.started") {
      audioOutputRef.current = true;
      setVoiceStatus("speaking");
      return;
    }
    if (type === "output_audio_buffer.stopped" || type === "output_audio_buffer.cleared") {
      audioOutputRef.current = false;
      if (playbackPendingRef.current && listenLoopRef.current) {
        playbackPendingRef.current = false;
        clientRef.current.setMicrophoneEnabled(true);
        setVoiceStatus("listening");
      }
      return;
    }
    if (type === "response.function_call_arguments.done") {
      const callId = event.call_id;
      if (!callId || !event.name || handledCallsRef.current.has(callId) || isAssistantUsageBlocked(usageRef.current)) return;
      handledCallsRef.current.add(callId);
      toolActiveRef.current = true;
      setVoiceStatus("thinking");
      const id = assistantIdRef.current || `assistant-${Date.now()}`;
      assistantIdRef.current = id;
      updateLiveMessage(id, { role: "assistant", text: "Searching...", isStreaming: true });
      let args = {};
      try { const parsed = JSON.parse(event.arguments || "{}"); if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) args = parsed; } catch { /* Use an empty object for invalid arguments. */ }
      let result;
      try { result = await runAssistantTool(event.name, args); }
      catch (error) { result = { error: true, message: error.message || "Tool request failed." }; }
      if (!matchesSession()) return;
      clientRef.current.send({ type: "conversation.item.create", item: { type: "function_call_output", call_id: callId, output: JSON.stringify(result) } });
      toolActiveRef.current = false;
      awaitingToolResponseRef.current = true;
      clientRef.current.send({ type: "response.create", response: { output_modalities: ["audio"] } });
      return;
    }
    if (type === "response.done") {
      if (event.response?.id && assistantIdRef.current && event.response.id !== assistantIdRef.current) return;
      responseActiveRef.current = false;
      if (toolActiveRef.current || awaitingToolResponseRef.current) return;
      const status = event.response?.status;
      if (status && status !== "completed") setVoiceError(`Assistant response ${status}.`);
      if (status === "completed" && audioOutputRef.current) {
        playbackPendingRef.current = true;
      } else if (listenLoopRef.current) {
        clientRef.current.setMicrophoneEnabled(true);
        setVoiceStatus("listening");
      }
      return;
    }
    if (type === "error") {
      if (/no active response found/i.test(event.error?.message || "")) return;
      responseActiveRef.current = false;
      setVoiceError(event.error?.message || "Voice connection error.");
      setVoiceStatus("idle");
    }
  }, [loadHistory, queueSave, updateLiveMessage]);

  const startVoice = useCallback(async () => {
    if (sessionStatus !== "ready") return;
    if (clientRef.current?.ready) {
      listenLoopRef.current = true;
      clientRef.current.setMicrophoneEnabled(true);
      setMicMuted(false);
      setVoiceStatus("listening");
      return;
    }
    const session = sessionRef.current;
    if (!session) return;
    setVoiceError("");
    setVoiceStatus("connecting");
    listenLoopRef.current = true;
    const generation = generationRef.current;
    const client = new BrowserRealtimeVoice({
      onEvent: (event) => { void handleRealtimeEvent(event); },
      onDisconnect: () => {
        if (generation === generationRef.current) {
          setSessionError("Voice connection ended. Start a new chat to reconnect.");
          setSessionStatus("error");
          setVoiceStatus("idle");
          client.close();
        }
      },
      onPlaybackError: setVoiceError,
    });
    clientRef.current = client;
    try {
      await client.connect(session);
      if (generation === generationRef.current && !client.closed && !responseActiveRef.current) {
        setVoiceStatus(session.assistant_greeting ? "thinking" : "listening");
      }
    } catch (error) {
      if (generation === generationRef.current) {
        setVoiceError(error.message || "Could not start the microphone.");
        setVoiceStatus("idle");
        listenLoopRef.current = false;
        clientRef.current = null;
      }
    }
  }, [handleRealtimeEvent, sessionStatus]);

  const handleOrbClick = () => {
    if (voiceStatus === "listening") {
      listenLoopRef.current = false;
      turnRevisionRef.current += 1;
      if (responseTimerRef.current) clearTimeout(responseTimerRef.current);
      responseTimerRef.current = null;
      clientRef.current?.setMicrophoneEnabled(false);
      setVoiceStatus("idle");
    } else if (voiceStatus === "thinking" || voiceStatus === "speaking") {
      listenLoopRef.current = true;
      clientRef.current?.interrupt();
      responseActiveRef.current = false;
      setVoiceStatus("listening");
    } else if (voiceStatus === "idle") {
      void startVoice();
    }
  };

  useEffect(() => {
    const conversation = conversationRef.current;
    if (conversation) conversation.scrollTop = conversation.scrollHeight;
  }, [messages, liveMessages]);

  useEffect(() => {
    const onResize = () => {
      const narrow = window.innerWidth <= 900;
      setIsNarrowViewport(narrow);
      if (!narrow) setMobileSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isNarrowViewport || !mobileSidebarOpen) return undefined;
    historyCloseRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileSidebarOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isNarrowViewport, mobileSidebarOpen]);

  const historyVisible = isNarrowViewport ? mobileSidebarOpen : sidebarOpen;
  const previousHistoryVisibleRef = useRef(historyVisible);
  useEffect(() => {
    if (previousHistoryVisibleRef.current && !historyVisible) historyOpenRef.current?.focus();
    previousHistoryVisibleRef.current = historyVisible;
  }, [historyVisible]);
  const openHistorySidebar = () => {
    if (isNarrowViewport) setMobileSidebarOpen(true);
    else setSidebarOpen(true);
  };
  const closeHistorySidebar = () => {
    if (isNarrowViewport) setMobileSidebarOpen(false);
    else setSidebarOpen(false);
  };

  const filteredSessions = useMemo(() => sessions.filter((session) =>
    preview(session).toLowerCase().includes(search.toLowerCase())
  ), [sessions, search]);

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessage(key);
    } catch {
      setCopiedMessage(null);
    }
  };

  const currentStatus = sessionStatus === "blocked" ? "Access limited" : sessionStatus === "loading" ? "Connecting..." : sessionStatus === "error" ? "Offline"
    : voiceStatus === "connecting" ? "Connecting voice..." : voiceStatus === "listening" ? micMuted ? "Listening muted" : "Listening"
      : voiceStatus === "thinking" ? "Thinking" : voiceStatus === "speaking" ? "Speaking" : "Tap to speak";
  const currentDescription = sessionStatus === "loading" ? "Checking your voice access..."
    : sessionStatus === "blocked" || sessionStatus === "error" ? sessionError
      : voiceError || (voiceStatus === "listening" ? "Tap the microphone to stop listening." : VOICE_MESSAGE);

  return <HomeDashboardLayout showHeader={false} variant="voice-chat-layout">
    {({ toggleSidebar, isSidebarOpen }) => <>
    <div className="page-padding voiceHeader" inert={isNarrowViewport && mobileSidebarOpen}><button type="button" className="custom-menu-btn" aria-label={isSidebarOpen ? "Close menu" : "Open menu"} aria-expanded={isSidebarOpen} onClick={toggleSidebar}><i className="bi bi-filter-left" /></button></div>
    <div className={["chatgpt-app", !sidebarOpen && "chatgpt-sidebar-collapsed", mobileSidebarOpen && "chatgpt-mobile-sidebar-open"].filter(Boolean).join(" ")}>
      {isNarrowViewport && mobileSidebarOpen && <button type="button" className="chatgpt-mobile-overlay" aria-label="Dismiss conversation history" onClick={closeHistorySidebar} />}
      <aside id="voice-conversations-sidebar" className="chatgpt-sidebar" aria-label="Assistant Conversations" aria-hidden={!historyVisible} inert={!historyVisible}><div className="chatgpt-sidebar-content">
        <div className="assistant-sidebar-header"><div className="assistant-sidebar-heading"><h3>Assistant Conversations</h3><button ref={historyCloseRef} type="button" className="assistant-sidebar-toggle" aria-label="Close conversation history" onClick={closeHistorySidebar}><i className="bi bi-x-lg" /></button></div><p>Your previous realtime voice sessions.</p><button type="button" className="voice-history-refresh" onClick={loadHistory} disabled={historyStatus === "loading"}>Refresh history</button></div>
        <button type="button" className="assistant-new-chat" onClick={startNewSession} disabled={sessionStatus === "loading"}><i className="bi bi-chat-left" /><span>New chat</span></button>
        <div className="chatgpt-search"><i className="bi bi-search" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search conversations" aria-label="Search conversations" />{search && <button type="button" aria-label="Clear search" onClick={() => setSearch("")}><i className="bi bi-x-lg" /></button>}</div>
        <div className="assistant-conversation-scroll"><div className="assistant-conversation-list">
          {historyStatus === "loading" && <p className="voice-inline-status">Loading conversations...</p>}
          {historyStatus === "error" && <div className="voice-inline-status" role="alert"><p>{historyError}</p><button type="button" onClick={loadHistory}>Retry</button></div>}
          {historyStatus === "success" && filteredSessions.map((session) => <button type="button" key={session.session_log_id} className={`assistant-conversation-item ${selectedHistoryId === session.session_log_id ? "is-active" : ""}`} onClick={() => openConversation(session.session_log_id)}><span className="assistant-conversation-title">{preview(session)}</span><span className="assistant-conversation-date">{relativeDate(session.latest_message?.created_at || session.created_at)}</span></button>)}
          {historyStatus === "success" && filteredSessions.length === 0 && <p className="chatgpt-no-results">{search ? "No matching conversations" : "No conversations yet"}</p>}
        </div></div>
      </div></aside>
      <div className="chatgpt-main" inert={isNarrowViewport && mobileSidebarOpen}>
        <header className="chatgpt-topbar"><strong className="voice-page-title">{selectedHistoryId == null ? "Chat Voice" : "Conversation History"}</strong><div className="chatgpt-topbar-spacer" />{selectedHistoryId != null && <button type="button" className="voice-live-button" onClick={startNewSession}>Back to Chat Voice</button>}{!historyVisible && <button ref={historyOpenRef} type="button" className="chatgpt-icon-button reopen-sidebar" aria-label="Open conversation history" aria-controls="voice-conversations-sidebar" aria-expanded={false} onClick={openHistorySidebar}><i className="bi bi-layout-sidebar-reverse" /></button>}</header>
        <section ref={conversationRef} className={`chatgpt-conversation ${(selectedHistoryId == null ? liveMessages : messages).length ? "has-messages" : "is-empty"}`} aria-live="polite">
          {selectedHistoryId != null && conversationStatus === "loading" && <div className="voice-panel-status">Loading messages...</div>}
          {selectedHistoryId != null && conversationStatus === "error" && <div className="voice-panel-status" role="alert"><p>{conversationError}</p><button type="button" onClick={() => openConversation(selectedHistoryId)}>Retry</button></div>}
          {selectedHistoryId != null && conversationStatus === "success" && messages.length === 0 && <div className="voice-panel-status">No messages found.</div>}
          {selectedHistoryId != null && messages.length > 0 && <div className="chatgpt-messages">{messages.map((message, index) => {
            const key = `${selectedHistoryId}-${message.sequence ?? index}`;
            return <div key={key} className={`chatgpt-message-row ${message.role === "user" ? "user" : "assistant"}`}>{message.role === "user" ? <div className="chatgpt-user-bubble"><span className="voice-message-label">You</span>{message.message}</div> : <div className="chatgpt-assistant-message"><span className="voice-message-label">Assistant</span><div className="chatgpt-message-text">{message.message}</div><div className="chatgpt-message-actions"><button type="button" title="Copy" onClick={() => copyText(message.message, key)}><i className={`bi ${copiedMessage === key ? "bi-check2" : "bi-copy"}`} /></button></div></div>}</div>;
          })}</div>}
          {selectedHistoryId == null && liveMessages.length > 0 && <div className="chatgpt-messages">{liveMessages.map((message) => <div key={message.id} className={`chatgpt-message-row ${message.role}`}>
            {message.role === "user" ? <div className="chatgpt-user-bubble"><span className="voice-message-label">You</span>{message.text}</div>
              : <div className="chatgpt-assistant-message"><span className="voice-message-label">Assistant</span><div className="chatgpt-message-text">{message.text}</div></div>}
          </div>)}</div>}
          {selectedHistoryId == null && liveMessages.length === 0 && <div className="chatgpt-empty-state"><h1>What can I help with?</h1>{sessionLogId != null && usage?.daily_credit_limit > 0 && <p className="voice-credit-note">{usage.remaining_credits_today ?? 0} credits remaining today</p>}</div>}
        </section>
        <footer className="chatgpt-composer-area"><div className="voice-assistant-container"><div className="voice-assistant-content"><button type="button" className={`voice-orb ${voiceStatus === "listening" ? "voice-orb-active" : ""}`} disabled={sessionStatus !== "ready" || selectedHistoryId != null || voiceStatus === "connecting"} onClick={handleOrbClick} aria-label={voiceStatus === "listening" ? "Stop listening" : "Start speaking"}><span className="voice-mic-circle"><i className="bi bi-mic-fill" /></span></button><h3 className="voice-assistant-title">{selectedHistoryId == null ? currentStatus : "Read-only history"}</h3><p className="voice-assistant-description">{selectedHistoryId == null ? currentDescription : "Start a new chat to begin another voice session."}</p>{voiceStatus === "listening" && selectedHistoryId == null && <button type="button" className="voice-retry-button" onClick={() => { const next = !micMuted; setMicMuted(next); clientRef.current?.setMicrophoneEnabled(!next); }}>{micMuted ? "Unmute microphone" : "Mute microphone"}</button>}{sessionStatus === "error" && selectedHistoryId == null && <button type="button" className="voice-retry-button" onClick={startNewSession}>Retry session</button>}</div></div></footer>
      </div>
    </div>
    </>}
  </HomeDashboardLayout>;
}
